// The purpose of this module is to register the custom control validation schemas without circular dependencies.
// It also provides functions for getting validation schemas from the registry after the registrations have been done.
import type { IObjectSchema, Joi } from '@ksyos/koi';
import type { CustomControlCode, ValidationSchemaVersion } from '@common/enums';
import type { ISchemaContainer } from '@common/validation/schemaContainer';
import '@common/validation/customControls/register';
import type { RegistryEntry } from './serviceActivityRegistry';
import { ServiceActivityRegistry } from './serviceActivityRegistry';

export function getRegistryEntry<Data extends Record<string, unknown> = Record<string, unknown>>(
    customControlCode: CustomControlCode,
    version: number
): RegistryEntry<Data> {
    const registry = ServiceActivityRegistry.instance.getRegistry();
    const entry = registry.get(customControlCode)?.get(version);
    if (!entry) {
        throw new Error(`No register entry for code: ${customControlCode} and version: ${version}`);
    }

    return entry;
}

export function getCustomControlValidationSchema(
    code: CustomControlCode,
    validationSchemaVersion: ValidationSchemaVersion,
    customControlVersion: number
): IObjectSchema {
    return getRegistryEntry(code, customControlVersion)[validationSchemaVersion];
}

export function getCustomControlValidationSchemaContainer<T>(
    customControlCode: CustomControlCode,
    validationSchemaVersion: ValidationSchemaVersion,
    customControlVersion: number
): ISchemaContainer<T> {
    return {
        schema: getCustomControlValidationSchema(customControlCode, validationSchemaVersion, customControlVersion),
    };
}

export function validateCustomControlData(
    customControlCode: CustomControlCode,
    data: unknown,
    validationSchemaVersion: ValidationSchemaVersion,
    customControlVersion: number
): Joi.ValidationResult {
    const schema = getCustomControlValidationSchema(customControlCode, validationSchemaVersion, customControlVersion);

    return schema.validate(data, {
        abortEarly: false,
        convert: false,
        presence: 'required',
        stripUnknown: {
            arrays: false,
            objects: true,
        },
    });
}

/*
 * Warning: You might be looking for `ProcessActivityService.getValidatedCustomControlDataWithinServiceContext`
 * See https://ksyos-it.atlassian.net/browse/CS-24924
 */
export function getValidatedCustomControlData<T>(
    customControlCode: CustomControlCode,
    data: unknown,
    validationSchemaVersion: ValidationSchemaVersion,
    customControlVersion: number
): T {
    const { error, value } = validateCustomControlData(
        customControlCode,
        data,
        validationSchemaVersion,
        customControlVersion
    );
    if (error) {
        throw error;
    }

    return value as T;
}
